/**
 * package-blocks-prompt.ts
 * Pure prompt-opbouw + antwoord-parsing voor de per-pakket offerteteksten.
 * Bewust los van api.ts/supabase zodat de helpers te unit-testen zijn zonder
 * de Supabase-client (die import.meta.env vereist) mee te trekken.
 */

import { cleanText } from './ai-presentation';
import type { GiftPackage, GiftPackageItem, PresentationExample } from './types';

export interface GeneratePackageBlocksInput {
  pkg: GiftPackage;
  items: GiftPackageItem[];
  /** Klant-randvoorwaarden (vega, geen alcohol, duurzaam, budget). Stuurt de toon. */
  uitgangspunten?: string;
  /** Titel van het gekozen goede doel, puur als context (de tekst komt uit de bibliotheek). */
  goedDoelTitle?: string;
}

export interface PackageBlocks {
  kenmerken: string[];
  over_het_pakket: string;
}

export const PACKAGE_BLOCKS_SYSTEM_PROMPT = `Je schrijft de per-pakket tekst voor een offerte van 2BMade in Leeuwarden, dat relatiegeschenk-pakketten samenstelt voor zakelijke klanten.

Je levert ALLEEN deze twee onderdelen voor ÉÉN pakket:
1. "Voornaamste kenmerken": 3 tot 4 korte krachtige punten. Geen hele zinnen, telegramstijl, elk punt 1 regel.
2. "Over het pakket": 1 verhalende alinea (4 tot 7 zinnen) die de inhoud beschrijft alsof je het in een gesprek vertelt. Noem producten, merken en leveranciers waar dat de tekst rijker maakt, maar maak er GEEN droge opsomming of tabel van.

Je schrijft NIET de opening, de historie of de afsluiting van de offerte. Die doet Mark zelf. Je schrijft ook NIET de "Over het goede doel"-tekst; die wordt apart ingevoegd.

Stijl:
- Normaal, warm, zakelijk Nederlands. Korte zinnen.
- Geen em-dashes. Geen woorden als 'leverage', 'robuust', 'naadloos', 'cruciaal', 'vibrant'.
- Geen markdown, geen kopjes.
- Houd rekening met de klant-uitgangspunten als die gegeven zijn (bijvoorbeeld vegetarisch, geen alcohol, gezond, duurzaam).

Antwoord ALLEEN met JSON, exact deze vorm:
{"kenmerken": ["punt 1", "punt 2", "punt 3"], "over_het_pakket": "een alinea"}`;

function describeItems(items: GiftPackageItem[]): string {
  const lines: string[] = [];
  for (const it of items) {
    if (it.excluded) continue;
    const p = it.gift_product;
    if (!p) continue;
    const parts = [`${it.quantity_per_package}x ${p.name}`];
    if (p.brand && p.brand !== p.name) parts.push(`merk ${p.brand}`);
    if (p.supplier?.name) parts.push(`leverancier ${p.supplier.name}`);
    if (p.is_packaging) parts.push('(verpakking)');
    lines.push('- ' + parts.join(', '));
  }
  return lines.join('\n');
}

/** Bouwt de user-message. Pure functie zodat de prompt-opbouw los te testen is. */
export function buildPackageBlocksMessage(
  input: GeneratePackageBlocksInput,
  examples: PresentationExample[]
): string {
  const { pkg, items, uitgangspunten, goedDoelTitle } = input;
  const blocks: string[] = [];

  if (pkg.theme) blocks.push(`Thema: ${pkg.theme}`);
  if (pkg.season) blocks.push(`Seizoen: ${pkg.season}`);
  if (uitgangspunten?.trim()) blocks.push(`Uitgangspunten van de klant:\n${uitgangspunten.trim()}`);
  if (goedDoelTitle?.trim()) {
    blocks.push(
      `Aan dit pakket is het goede doel "${goedDoelTitle.trim()}" gekoppeld. Verwijs er ` +
        `hooguit kort naar; de uitleg over het goede doel wordt apart ingevoegd.`
    );
  }
  blocks.push(`Inhoud van het pakket (verwerk verhalend, niet als lijst):\n${describeItems(items)}`);

  if (examples.length > 0) {
    const shots = examples
      .slice(0, 2)
      .map((e, i) => `Voorbeeld ${i + 1} (${e.title}):\n${e.body.trim().slice(0, 1200)}`)
      .join('\n\n');
    blocks.push(
      `Hieronder eerdere offerteteksten in onze huisstijl. Volg deze toon, structuur en ` +
        `woordkeuze, maar neem de inhoud NIET letterlijk over:\n\n${shots}`
    );
  }

  return blocks.filter(Boolean).join('\n\n');
}

/**
 * Parseert het AI-antwoord naar PackageBlocks. Tolerant: pakt het eerste JSON-object,
 * schoont tekst op met cleanText, en garandeert een geldige vorm.
 */
export function parsePackageBlocks(raw: string): PackageBlocks {
  const match = raw.match(/\{[\s\S]*\}/);
  if (!match) throw new Error('AI gaf geen JSON terug');
  const parsed = JSON.parse(match[0]) as Partial<PackageBlocks>;
  const kenmerken = Array.isArray(parsed.kenmerken)
    ? parsed.kenmerken.map((k) => cleanText(String(k))).filter(Boolean)
    : [];
  const over = typeof parsed.over_het_pakket === 'string' ? cleanText(parsed.over_het_pakket) : '';
  if (!over && kenmerken.length === 0) throw new Error('AI-antwoord bevatte geen bruikbare tekst');
  return { kenmerken, over_het_pakket: over };
}
